import { useHandler } from "@redotech/react-util/hook";
import {
  type ComputerPrintEngines,
  PrintEngineRecommendationDismissal,
  recommendedPrintEngine,
} from "@redotech/redo-fulfillment-group-common/print-engine";
import { useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useState } from "react";

const PrintEngineWarningIcon = memo(function PrintEngineWarningIcon() {
  return <RedoIcon color="warning" Icon={AlertTriangleIcon} size={16} />;
});

export interface PrintEngineRecommendation {
  computerId: number;
  computerName: string;
  engine: string;
  recommendedEngine: string;
}

export function recommendationForPrinters(
  engines: ComputerPrintEngines,
  printers: readonly { id: number; computer: { id: number; name: string } }[],
  printerIds: readonly number[],
  dismissedComputerIds: ReadonlySet<number>,
): PrintEngineRecommendation | null {
  for (const printerId of printerIds) {
    const printer = printers.find((candidate) => candidate.id === printerId);
    if (!printer || dismissedComputerIds.has(printer.computer.id)) {
      continue;
    }
    const recorded = engines[String(printer.computer.id)];
    if (!recorded || recorded.ignored) {
      continue;
    }
    const recommended = recommendedPrintEngine(recorded.engine);
    if (!recommended) {
      continue;
    }
    return {
      computerId: printer.computer.id,
      computerName: printer.computer.name,
      engine: recorded.engine,
      recommendedEngine: recommended,
    };
  }
  return null;
}

export interface PrintEngineRecommendationWarningProps {
  recommendation: PrintEngineRecommendation;
  onDismiss(dismissal: PrintEngineRecommendationDismissal): void;
}

export const PrintEngineRecommendationWarning = memo(
  function PrintEngineRecommendationWarning({
    recommendation,
    onDismiss,
  }: PrintEngineRecommendationWarningProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const tooltip = t(
      "{{computerName}} printed with {{engine}}. {{recommendedEngine}} may be faster.",
      {
        computerName: recommendation.computerName,
        engine: recommendation.engine,
        recommendedEngine: recommendation.recommendedEngine,
      },
    );

    const close = useHandler(() => {
      setOpen(false);
    });
    const dismiss = useHandler(
      (dismissal: PrintEngineRecommendationDismissal) => {
        setOpen(false);
        onDismiss(dismissal);
      },
    );

    return (
      <>
        <Tooltip leftAlign title={tooltip}>
          <Flex w="fit-content">
            <RedoButton
              hierarchy="tertiary"
              IconLeading={PrintEngineWarningIcon}
              onClick={() => setOpen(true)}
              size="xs"
            />
          </Flex>
        </Tooltip>
        {open ? (
          <RedoModal
            modalSize="sm"
            onModalCloseRequested={close}
            primaryButton={{
              onClick: () => dismiss(PrintEngineRecommendationDismissal.DONE),
              text: t("Done"),
            }}
            secondaryButton={{
              onClick: () => dismiss(PrintEngineRecommendationDismissal.IGNORE),
              text: t("Ignore"),
            }}
            subtitle={t("{{computerName}} last printed with {{engine}}.", {
              computerName: recommendation.computerName,
              engine: recommendation.engine,
            })}
            theme="warn"
            title={t("{{recommendedEngine}} may be faster", {
              recommendedEngine: recommendation.recommendedEngine,
            })}
            TitleIcon={AlertTriangleIcon}
          >
            <Flex align="center" gap="md">
              <Text fontSize="sm" fontWeight="medium" textColor="primary">
                {t("Redo Connect")}
              </Text>
              <RedoIcon color="tertiary" Icon={ArrowRightIcon} size={14} />
              <Text fontSize="sm" fontWeight="medium" textColor="primary">
                {t("Printers")}
              </Text>
              <RedoIcon color="tertiary" Icon={ArrowRightIcon} size={14} />
              <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                {recommendation.recommendedEngine}
              </Text>
            </Flex>
          </RedoModal>
        ) : null}
      </>
    );
  },
);
